import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";

// ── Design tokens — Minimal + Sunshine + Fraunces/Jakarta ──
const C = {
  bg: "#ffffff",
  surface: "#f7f7f7",
  peach: "#ECDBC9",
  grey: "#CDCDCD",
  border: "#e0e0e0",
  text: "#000000",
  muted: "#555555",
  dim: "#888888",
  accent: "#52658F",
  navy: "#000000",
};
const SYNE = "'Fraunces', serif";
const MONO = "'Plus Jakarta Sans', sans-serif";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";

const LANDING_TRANSLATIONS = {
    en: {
        getStarted: "Get Started",
        heroBadge: "Data Gets Balanced",
        heroCTA: "Try It Free →",
        problemsTitle: "Sound familiar?",
        problems: [
            { icon: "conflict", title: "Two systems, two different numbers", description: "Your team stays late manually checking rows in Excel" },
            { icon: "search",   title: "You can't find where it breaks", description: "Hours of searching and still no root cause" },
            { icon: "clock",    title: "Reports are delayed", description: "Someone is waiting but the data isn't reconciled yet" },
            { icon: "shield",   title: "Fraud slips through undetected", description: "Without proper checks, duplicate transactions and suspicious entries stay hidden in the gaps" },
        ],
        ctaTitle: "Ready to stop reconciling manually?",
        ctaSubtitle: "No setup, no spreadsheets. Just upload and go.",
        ctaButton: "Sign Up Free →",
        heroTitle: "Stop wasting hours on data that doesn't match.",
        heroSubtitle: "Upload any two files — DGB finds every difference and tells you exactly why.",
        heroSocialProof: "Used by operations, finance, and business teams across any industry.",
        heroSecondaryCTA: "See how it works",
        featuresTitle: "Everything you need to check your numbers faster",
        featuresSubtitle: "Built for anyone who has ever spent hours manually comparing two files.",
        feature1Title: "Finds every difference instantly",
        feature1Desc: "Upload two files and DGB automatically spots every row that doesn't match — no formulas, no VLOOKUP.",
        feature2Title: "Tells you exactly why",
        feature2Desc: "AI explains each discrepancy in plain language. Not just 'numbers differ' — but what likely caused it.",
        feature3Title: "Works even when files look different",
        feature3Desc: "One file per order, another per item? DGB handles different levels of detail automatically before comparing.",
        feature4Title: "Supports CSV, Excel & Google Sheets",
        feature4Desc: "Upload from any system — or paste a Google Sheets link. Multiple sheets, mixed formats, messy data — all handled.",
        privacyTitle: "Your data never leaves your browser.",
        privacySubtitle: "DGB is built with privacy by design. Files are processed locally — we never store, share, or train on your data.",
        privacy1Title: "Processed locally",
        privacy1Desc: "Your files are parsed and analysed entirely in your browser. Raw data is never transmitted to our servers.",
        privacy2Title: "Never stored",
        privacy2Desc: "We don't store your file contents. Only anonymized report summaries are saved for your history.",
        privacy3Title: "AI analysis",
        privacy3Desc: "AI explanations send only row-level summaries to Anthropic's API. Anthropic does not train on API data.",
        privacy4Title: "Indonesia compliant",
        privacy4Desc: "Built with awareness of Indonesia's Personal Data Protection Law (UU PDP 2022).",
        toolsMainTitle: "Check My Numbers",
        toolsMainDesc: "Upload two files — DGB finds every difference and explains what happened.",
        toolsMainCTA: "Start checking →",
        toolsAlsoAvailable: "Also available",
        toolsOutlierTitle: "Outlier Detection",
        toolsOutlierDesc: "Find anomalies in a single file",
        toolsRFMTitle: "Customer Segmentation",
        toolsRFMDesc: "Understand who your best customers are",
        howTitle: "How DGB works",
        how1Title: "Upload two files",
        how1Desc: "CSV, Excel or Google Sheets from any system — POS, ERP, accounting, banking, or anything else",
        how2Title: "DGB checks everything automatically",
        how2Desc: "Every difference found in seconds — no setup, no formulas",
        how3Title: "We explain what happened",
        how3Desc: "Plain language explanation for each row that doesn't match",
    },
    id: {
        getStarted: "Mulai Sekarang",
        heroBadge: "Data Gets Balanced",
        heroCTA: "Coba Gratis →",
        problemsTitle: "Familiar?",
        problems: [
            { icon: "conflict", title: "Dua sistem, dua angka berbeda", description: "Tim kamu lembur untuk mengecek baris demi baris di Excel" },
            { icon: "search",   title: "Tidak tahu di mana masalahnya", description: "Berjam-jam mencari tapi akar masalah tidak ditemukan" },
            { icon: "clock",    title: "Laporan terlambat", description: "Ada yang menunggu tapi data belum selesai dicek" },
            { icon: "shield",   title: "Fraud tidak terdeteksi", description: "Tanpa pengecekan yang benar, transaksi duplikat dan entri mencurigakan tersembunyi di celah data" },
        ],
        ctaTitle: "Siap berhenti rekonsiliasi manual?",
        ctaSubtitle: "Tanpa setup, tanpa spreadsheet. Upload dan langsung jalan.",
        ctaButton: "Daftar Gratis →",
        heroTitle: "Berhenti buang waktu gara-gara data yang tidak cocok.",
        heroSubtitle: "Upload dua file apa saja — DGB menemukan setiap perbedaan dan menjelaskan alasannya.",
        heroSocialProof: "Dipakai oleh tim operasional, keuangan, dan bisnis di berbagai industri.",
        heroSecondaryCTA: "Lihat cara kerjanya",
        featuresTitle: "Semua yang kamu butuhkan untuk cek data lebih cepat",
        featuresSubtitle: "Dibuat untuk siapa saja yang pernah habiskan berjam-jam membandingkan dua file secara manual.",
        feature1Title: "Menemukan setiap perbedaan secara instan",
        feature1Desc: "Upload dua file dan DGB otomatis menemukan setiap baris yang tidak cocok — tanpa formula, tanpa VLOOKUP.",
        feature2Title: "Menjelaskan alasannya dengan tepat",
        feature2Desc: "AI menjelaskan setiap perbedaan dalam bahasa yang mudah dipahami. Bukan sekadar 'angka berbeda' — tapi apa yang menyebabkannya.",
        feature3Title: "Bekerja meski file terlihat berbeda",
        feature3Desc: "Satu file per order, satu lagi per item? DGB menangani perbedaan level detail secara otomatis sebelum membandingkan.",
        feature4Title: "Mendukung CSV, Excel & Google Sheets",
        feature4Desc: "Upload dari sistem apapun — atau tempel link Google Sheets. Multi-sheet, format campuran, data berantakan — semua ditangani.",
        privacyTitle: "Data kamu tidak pernah meninggalkan browser.",
        privacySubtitle: "DGB dibangun dengan prinsip privacy by design. File diproses secara lokal — kami tidak pernah menyimpan, membagikan, atau melatih model dengan data kamu.",
        privacy1Title: "Diproses secara lokal",
        privacy1Desc: "File kamu dianalisis sepenuhnya di browser. Data mentah tidak pernah dikirim ke server kami.",
        privacy2Title: "Tidak pernah disimpan",
        privacy2Desc: "Kami tidak menyimpan isi file kamu. Hanya ringkasan laporan anonim yang disimpan untuk riwayat.",
        privacy3Title: "Analisis AI",
        privacy3Desc: "Penjelasan AI hanya mengirim ringkasan baris ke API Anthropic. Anthropic tidak melatih model dengan data API.",
        privacy4Title: "Sesuai regulasi Indonesia",
        privacy4Desc: "Dibangun dengan memperhatikan Undang-Undang Pelindungan Data Pribadi (UU PDP 2022).",
        toolsMainTitle: "Cek Angka Saya",
        toolsMainDesc: "Upload dua file — DGB menemukan setiap perbedaan dan menjelaskan apa yang terjadi.",
        toolsMainCTA: "Mulai cek →",
        toolsAlsoAvailable: "Fitur lainnya",
        toolsOutlierTitle: "Deteksi Outlier",
        toolsOutlierDesc: "Temukan anomali dalam satu file",
        toolsRFMTitle: "Segmentasi Pelanggan",
        toolsRFMDesc: "Pahami siapa pelanggan terbaik kamu",
        howTitle: "Cara kerja DGB",
        how1Title: "Upload dua file",
        how1Desc: "CSV, Excel atau Google Sheets dari sistem apapun — POS, ERP, akuntansi, perbankan, atau lainnya",
        how2Title: "DGB mengecek semuanya secara otomatis",
        how2Desc: "Setiap perbedaan ditemukan dalam hitungan detik — tanpa setup, tanpa formula",
        how3Title: "Kami jelaskan apa yang terjadi",
        how3Desc: "Penjelasan dalam bahasa yang mudah dipahami untuk setiap baris yang tidak cocok",
    }
};

// ── Responsive helpers ───────────────────────
const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
const px = isMobile ? "1.25rem" : "4rem";
function Icon({ name, size = 28, color = "#52658F" }) {
  const icons = {
    conflict: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
    search:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>,
    clock:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    shield:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    balance:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    outlier:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    segment:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    flash:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    ai:       <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10"/><path d="M12 8v4l3 3"/><circle cx="18" cy="6" r="3" fill={color} fillOpacity="0.2"/><path d="M18 4v4M16 6h4"/></svg>,
    files:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
    globe:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    lock:     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
    block:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>,
    robot:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="15" x2="8" y2="17"/><line x1="16" y1="15" x2="16" y2="17"/></svg>,
    flag:     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
  };
  return icons[name] || null;
}
function Navbar({ lang, setLang, t }) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setUser(data.session?.user || null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  window.onscroll = () => setScrolled(window.scrollY > 50);

  return (
    <nav style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "1rem 2rem", position: "fixed", top: 0, width: "100%",
      flexWrap: "wrap", gap: "0.5rem",
      background: scrolled ? "rgba(0,0,0,0.97)" : "#000000",
      borderBottom: scrolled ? "1px solid #222" : "none",
      transition: "all 0.3s", boxSizing: "border-box", zIndex: 10, fontFamily: MONO,
    }}>
      <img src={require("../assets/dgb-logo-dark.png")} alt="DGB"
        style={{ height: 80, cursor: "pointer", marginLeft: "-16px", marginTop: "-4px", marginBottom: "-4px" }}
        onClick={() => navigate("/")} />
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button onClick={() => navigate("/pricing")}
          style={{ background: "transparent", border: "none", color: "#888", fontFamily: MONO, fontSize: "0.85rem", cursor: "pointer" }}>
          Pricing
        </button>
        <button onClick={() => setLang(lang === "en" ? "id" : "en")}
          style={{ background: "transparent", border: "1px solid #333", borderRadius: 6, padding: "0.35rem 0.7rem", fontFamily: MONO, fontSize: "0.72rem", color: "#888", cursor: "pointer" }}>
          {lang === "en" ? "ID 🌐" : "EN 🌐"}
        </button>
        {user ? (
          <>
            <button onClick={() => navigate("/app")}
              style={{ background: "transparent", border: "1px solid #333", color: "#aaa", padding: "0.55rem 1.25rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 600, cursor: "pointer", fontSize: "0.85rem" }}>
              Go to app
            </button>
            <button onClick={() => navigate("/account")}
              style={{ background: C.accent, color: "white", border: "none", padding: "0.55rem 1.25rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 600, cursor: "pointer", fontSize: "0.85rem" }}>
              Account
            </button>
          </>
        ) : (
          <button onClick={() => navigate("/login")}
            style={{ background: C.accent, color: "white", border: "none", padding: "0.55rem 1.5rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 700, cursor: "pointer" }}>
            {t("getStarted")}
          </button>
        )}
      </div>
    </nav>
  );
}

// ── Hero ──────────────────────────────
function Hero({ t }) {
  const navigate = useNavigate();
  return (
    <section style={{ textAlign: "center", padding: `9rem ${px} 5rem`, maxWidth: 900, margin: "0 auto", fontFamily: MONO }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: C.peach, border: "1px solid #d8c4a8", color: "#5a3a1a", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.05em", padding: "0.35rem 1rem", borderRadius: 100, marginBottom: "2rem" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent, display: "inline-block" }} />
        {t("heroBadge")}
      </div>
      <h1 style={{ fontFamily: SYNE, fontSize: "clamp(2rem,4.5vw,3rem)", fontWeight: 800, lineHeight: 1.18, letterSpacing: "-0.02em", color: C.text, marginBottom: "1.5rem", maxWidth: "min(640px, 100%)", margin: "0 auto 1.5rem" }}>
        {t("heroTitle")}
      </h1>
      <p style={{ color: C.muted, fontSize: "1.1rem", lineHeight: 1.8, maxWidth: 520, margin: "0 auto 1rem", fontWeight: 400, fontFamily: MONO }}>
        {t("heroSubtitle")}
      </p>
      <p style={{ color: C.dim, fontSize: "0.85rem", marginBottom: "2.5rem", fontFamily: MONO }}>
        {t("heroSocialProof")}
      </p>
      <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
        <button onClick={() => navigate("/app")}
          style={{ background: C.accent, color: "white", border: "none", padding: "0.9rem 2.5rem", borderRadius: 8, fontFamily: SYNE, fontSize: "1rem", fontWeight: 700, cursor: "pointer" }}>
          {t("heroCTA")}
        </button>
        <button onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
          style={{ background: "transparent", color: C.text, border: "1.5px solid " + C.text, padding: "0.9rem 2rem", borderRadius: 8, fontFamily: SYNE, fontSize: "1rem", fontWeight: 600, cursor: "pointer" }}>
          {t("heroSecondaryCTA")}
        </button>
      </div>
    </section>
  );
}

// ── Tools ──────────────────────────────
function Tools({ t }) {
  const navigate = useNavigate();
  return (
    <section style={{ padding: `0 ${px} 3rem`, maxWidth: 1200, margin: "0 auto", fontFamily: MONO }}>
      <div onClick={() => navigate("/app")}
        style={{ background: "#000000", border: "1.5px solid #000000", borderRadius: 14, padding: "2rem 2.5rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1.5rem", flexWrap: "wrap", marginBottom: "1rem", transition: "background 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.background = "#111111"}
        onMouseLeave={e => e.currentTarget.style.background = "#000000"}>
        <div style={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
          <Icon name="balance" size={32} color="#ffffff" />
          <div>
            <div style={{ fontFamily: SYNE, fontWeight: 700, color: "#ffffff", fontSize: "1.2rem", marginBottom: "0.3rem" }}>{t("toolsMainTitle")}</div>
            <div style={{ fontSize: "0.875rem", color: "#888", fontFamily: MONO }}>{t("toolsMainDesc")}</div>
          </div>
        </div>
        <button onClick={e => { e.stopPropagation(); navigate("/app"); }}
          style={{ background: C.accent, color: "white", border: "none", padding: "0.65rem 1.5rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", whiteSpace: "nowrap" }}>
          {t("toolsMainCTA")}
        </button>
      </div>
      <div style={{ marginBottom: "0.5rem" }}>
        <div style={{ fontSize: "0.7rem", color: C.dim, fontFamily: SYNE, fontWeight: 600, letterSpacing: "0.1em", marginBottom: "0.6rem", paddingLeft: "0.25rem" }}>
          {t("toolsAlsoAvailable").toUpperCase()}
        </div>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          {[
            { icon: "outlier", titleKey: "toolsOutlierTitle", descKey: "toolsOutlierDesc", path: "/outlier" },
            { icon: "segment", titleKey: "toolsRFMTitle",     descKey: "toolsRFMDesc",     path: "/rfm" },
          ].map(tool => (
            <div key={tool.path} onClick={() => navigate(tool.path)}
              style={{ background: C.grey, border: "1px solid #c0c0c0", borderRadius: 12, padding: "1rem 1.25rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "0.75rem", flex: 1, minWidth: 200, transition: "border-color 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
              onMouseLeave={e => e.currentTarget.style.borderColor = "#c0c0c0"}>
              <Icon name={tool.icon} size={22} color={C.accent} />
              <div>
                <div style={{ fontFamily: SYNE, fontWeight: 700, color: C.text, fontSize: "0.85rem" }}>{t(tool.titleKey)}</div>
                <div style={{ fontSize: "0.75rem", color: C.muted, marginTop: "0.15rem" }}>{t(tool.descKey)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Problems ──────────────────────────────
function Problems({ t }) {
  return (
    <section style={{ padding: `5rem ${px}`, background: C.peach, fontFamily: MONO }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontFamily: SYNE, fontSize: "1.85rem", fontWeight: 800, color: C.text, marginBottom: "3rem", letterSpacing: "-0.02em" }}>{t("problemsTitle")}</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.25rem" }}>
          {t("problems").map((p, i) => (
            <div key={i} style={{ background: "white", border: "1px solid #d8c4a8", borderRadius: 14, padding: "1.75rem", textAlign: "left" }}>
              <div style={{ marginBottom: "1rem" }}><Icon name={p.icon} size={28} color={C.accent} /></div>
              <h3 style={{ color: C.text, fontSize: "0.95rem", fontWeight: 700, marginBottom: "0.5rem" }}>{p.title}</h3>
              <p style={{ color: C.muted, fontSize: "0.875rem", margin: 0, lineHeight: 1.6 }}>{p.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Features ──────────────────────────────
function Features({ t }) {
  const features = [
    { icon: "flash",  title: t("feature1Title"), description: t("feature1Desc") },
    { icon: "ai",     title: t("feature2Title"), description: t("feature2Desc") },
    { icon: "files",  title: t("feature3Title"), description: t("feature3Desc") },
    { icon: "globe",  title: t("feature4Title"), description: t("feature4Desc") },
  ];
  return (
    <section style={{ padding: `5rem ${px}`, maxWidth: 1200, margin: "0 auto", fontFamily: MONO }}>
      <div style={{ textAlign: "center", marginBottom: "3rem" }}>
        <h2 style={{ fontFamily: SYNE, fontSize: "1.85rem", fontWeight: 800, color: C.text, marginBottom: "0.75rem", letterSpacing: "-0.02em" }}>{t("featuresTitle")}</h2>
        <p style={{ color: C.muted, maxWidth: 480, margin: "0 auto", fontFamily: MONO }}>{t("featuresSubtitle")}</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.5rem" }}>
        {features.map((f, i) => (
          <div key={i} style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.75rem" }}>
            <div style={{ marginBottom: "1rem" }}><Icon name={f.icon} size={26} color={C.accent} /></div>
            <h3 style={{ fontFamily: SYNE, color: C.text, fontSize: "1rem", fontWeight: 700, marginBottom: "0.5rem" }}>{f.title}</h3>
            <p style={{ color: C.muted, fontSize: "0.875rem", lineHeight: 1.7, margin: 0 }}>{f.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── How It Works ──────────────────────────────
function HowItWorks({ t }) {
  const steps = [
    { num: "01", title: t("how1Title"), description: t("how1Desc") },
    { num: "02", title: t("how2Title"), description: t("how2Desc") },
    { num: "03", title: t("how3Title"), description: t("how3Desc") },
  ];
  return (
    <section id="how-it-works" style={{ padding: `5rem ${px}`, background: C.grey, fontFamily: MONO }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <h2 style={{ fontFamily: SYNE, fontSize: "1.85rem", fontWeight: 800, color: C.text, marginBottom: "3rem", letterSpacing: "-0.02em", textAlign: "center" }}>{t("howTitle")}</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.5rem" }}>
          {steps.map((s, i) => (
            <div key={s.num} style={{ textAlign: "left", padding: "2rem", background: "white", borderRadius: 14, border: "1px solid #b8b8b8" }}>
              <div style={{ fontFamily: SYNE, fontSize: "3rem", fontWeight: 800, color: C.accent, opacity: 0.45, marginBottom: "1rem", letterSpacing: "-0.03em" }}>{s.num}</div>
              <h3 style={{ color: C.text, fontSize: "1.1rem", fontWeight: 700, marginBottom: "0.6rem", fontFamily: MONO }}>{s.title}</h3>
              <p style={{ color: C.muted, fontSize: "0.9rem", margin: 0, fontFamily: MONO, lineHeight: 1.7 }}>{s.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Privacy ──────────────────────────────
function Privacy({ t }) {
  return (
    <section style={{ padding: `5rem ${px}`, maxWidth: 1200, margin: "0 auto", fontFamily: MONO }}>
      <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 16, padding: "2.5rem" }}>
        <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "1rem" }}>PRIVACY & DATA SECURITY</div>
        <h2 style={{ fontFamily: SYNE, fontSize: "1.5rem", fontWeight: 800, color: C.text, marginBottom: "1rem", letterSpacing: "-0.01em" }}>{t("privacyTitle")}</h2>
        <p style={{ color: C.muted, fontSize: "0.9rem", lineHeight: 1.8, marginBottom: "1.5rem", fontFamily: MONO }}>{t("privacySubtitle")}</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1.25rem" }}>
          {[
            { icon: "lock",  title: t("privacy1Title"), desc: t("privacy1Desc") },
            { icon: "block", title: t("privacy2Title"), desc: t("privacy2Desc") },
            { icon: "robot", title: t("privacy3Title"), desc: t("privacy3Desc") },
            { icon: "flag",  title: t("privacy4Title"), desc: t("privacy4Desc") },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
              <div style={{ marginTop: "0.1rem", flexShrink: 0 }}><Icon name={item.icon} size={20} color={C.accent} /></div>
              <div>
                <div style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "0.85rem", color: C.text, marginBottom: "0.25rem" }}>{item.title}</div>
                <div style={{ fontSize: "0.8rem", color: C.muted, lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Pricing Band ──────────────────────────────
function PricingBand() {
  const navigate = useNavigate();
  return (
    <section style={{ padding: `4rem ${px}`, background: C.accent, textAlign: "center", fontFamily: MONO }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <div style={{ fontFamily: SYNE, fontSize: "1.6rem", fontWeight: 700, color: "white", marginBottom: "0.75rem", letterSpacing: "-0.01em" }}>
          Simple, honest pricing
        </div>
        <div style={{ color: "#c0cce0", fontSize: "0.9rem", marginBottom: "1.5rem", lineHeight: 1.7 }}>
          Start free — no credit card required.<br />
          Pro and Enterprise plans available for teams that need more.
        </div>
        <button onClick={() => navigate("/pricing")}
          style={{ background: "#000000", color: "white", border: "none", padding: "0.8rem 2rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" }}>
          See pricing →
        </button>
      </div>
    </section>
  );
}

// ── CTA Section ──────────────────────────────
function CTA({ t }) {
  const navigate = useNavigate();
  return (
    <section id="cta" style={{ background: C.surface, borderTop: "1px solid " + C.border, borderBottom: "1px solid " + C.border, padding: `5rem ${px}`, textAlign: "center", fontFamily: MONO }}>
      <h2 style={{ fontFamily: SYNE, fontSize: "2.1rem", fontWeight: 800, color: C.text, marginBottom: "1rem", letterSpacing: "-0.02em" }}>{t("ctaTitle")}</h2>
      <p style={{ color: C.muted, marginBottom: "2rem", fontFamily: MONO }}>{t("ctaSubtitle")}</p>
      <button onClick={() => navigate("/login")}
        style={{ background: C.accent, color: "white", border: "none", padding: "0.9rem 2.5rem", borderRadius: 8, fontFamily: SYNE, fontSize: "1rem", fontWeight: 700, cursor: "pointer" }}>
        {t("ctaButton")}
      </button>
    </section>
  );
}

// ── Footer ──────────────────────────────
function Footer() {
  const navigate = useNavigate();
  return (
    <footer style={{ background: "#000000", padding: "1.75rem 2rem", textAlign: "center", fontFamily: MONO }}>
      <span style={{ color: "#555", fontSize: "0.8rem" }}>
        © 2026 DGB ·{" "}
        <span style={{ cursor: "pointer", color: "#555" }} onClick={() => navigate("/pricing")}>Pricing</span>
        {" · "}
        <a href="mailto:hello@datagetsbalanced.com" style={{ color: "#555", textDecoration: "none" }}>hello@datagetsbalanced.com</a>
        {" · "}
        Jakarta, Indonesia
      </span>
    </footer>
  );
}

export default function Landing() {
  const [lang, setLang] = useState("en");
  const t = (key) => LANDING_TRANSLATIONS[lang][key] || LANDING_TRANSLATIONS["en"][key] || key;

  useEffect(() => {
    if (!document.querySelector('link[href*="Fraunces"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = FONT_LINK;
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, overflowX: "hidden" }}>
      <Navbar lang={lang} setLang={setLang} t={t} />
      <Hero t={t} />
      <Tools t={t} />
      <Problems t={t} />
      <Features t={t} />
      <HowItWorks t={t} />
      <Privacy t={t} />
      <PricingBand />
      <CTA t={t} />
      <Footer />
    </div>
  );
}
